import axios from "axios";
import { API_ENDPOINTS, API_BASE_URL } from "../../../../js/api.js";

/**
 * Serviço de acompanhamentos do usuário logado.
 *
 * Mantém a forma do CadastroEmpresa (uma única rota dedicada) e cai
 * para a rota genérica `tca-empregador` quando a rota dedicada
 * não estiver disponível.
 *
 * Atualizado para consumir:
 *   - GET /tca-empregador/acompanhamentos/me   (rota dedicada nova)
 *     devolve cada item já com etapa_atual/label/cor prontos para o
 *     stepper. Não exige page/limit (quebravam com 500).
 *   - GET /tca-empregador/:id/status-completo  (detalhe individual)
 *     devolve etapa_atual, ultimo_evento, historico JSON completo.
 */

/**
 * Tenta extrair a URL final do endpoint de acompanhamentos, seja ele uma
 * função ou uma string pronta.
 */
function resolverEndpointAcompanhamentosMe() {
  const ep = API_ENDPOINTS?.acompanhamentosMe;
  if (!ep) return null;
  return typeof ep === "function" ? ep() : ep;
}

/**
 * Tenta extrair a URL do endpoint de status-completo para um id.
 */
function resolverEndpointStatusCompleto(id) {
  const ep = API_ENDPOINTS?.tca_empregador_status_completo;
  if (!ep) return null;
  if (typeof ep === "function") return ep(id);
  // fallback para string com placeholder ":id"
  if (typeof ep === "string") return ep.replace(":id", encodeURIComponent(id));
  return null;
}

export async function listarAcompanhamentosMe({
  token,
  empresaId = null,
  tcaId = null,
  busca = "",
} = {}) {
  if (!token) throw new Error("Token obrigatório");

  // 1. Tenta rota dedicada /tca-empregador/acompanhamentos/me
  //    O backend desta API já implementa essa rota e devolve cada item
  //    com etapa_atual/label/cor/status_label derivados.
  const endpointDedicado = resolverEndpointAcompanhamentosMe();
  if (endpointDedicado) {
    try {
      // CORREÇÃO: repassar busca/empresaId/tcaId para a rota dedicada.
      // Antes estava com `params: {}` (vazio), o que fazia o backend
      // retornar TODOS os acompanhamentos do usuário e ignorar o
      // termo que o usuário digitou (ex.: buscar "3514" trazia o
      // primeiro registro da lista, não o de ID 3514).
      const params = {};
      if (empresaId) params.empresa_id = empresaId;
      if (tcaId) params.tca_empregador_id = tcaId;
      if (busca) params.busca = busca;

      const resposta = await axios.get(endpointDedicado, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      return resposta.data;
    } catch (err) {
      // Em ambiente de homologação anterior à atualização da API, a rota
      // dedicada pode retornar 404. Cai no fallback abaixo.
      if (err?.response?.status && err.response.status !== 404) {
        // Erro diferente de 404 → propaga.
        if (err.response.status >= 500) {
          // 5xx: fallback silencioso (comportamento antigo preservado).
        } else {
          throw err;
        }
      }
    }
  }

  // 2. Fallback: rota tca-empregador (usada originalmente pelo projeto).
  // NAO enviamos `page`/`limit` porque o backend atual quebra com 500.
  //
  // Convenção do contrato (TcaEmpregadorController.index):
  //   - `empresa_id`           → filtra registros pela Empresa vinculada
  //   - `tca_empregador_id`    → filtra registros pelo próprio TCA Empregador
  //   - `busca`                → termo de pesquisa textual
  const params = {};
  if (empresaId) params.empresa_id = empresaId;
  if (tcaId) params.tca_empregador_id = tcaId;
  if (busca) params.busca = busca;

  const resposta = await axios.get(`${API_BASE_URL}/tca-empregador`, {
    params,
    headers: { Authorization: `Bearer ${token}` },
  });
  return resposta.data;
}

export async function listarAcompanhamentosFallback({
  token,
  busca,
  empresaId,
  tcaId,
} = {}) {
  if (!token) throw new Error("Token obrigatório");
  const params = {};
  if (empresaId) params.empresa_id = empresaId;
  if (tcaId) params.tca_empregador_id = tcaId;
  if (busca) params.busca = busca;

  const resposta = await axios.get(`${API_BASE_URL}/tca-empregador`, {
    params,
    headers: { Authorization: `Bearer ${token}` },
  });
  return resposta.data;
}


/**
 * Busca o status completo (etapa derivada, último evento e histórico JSON)
 * de UM cadastro específico. Substitui a necessidade de o frontend
 * adivinhar o status textual.
 *
 * Quando o backend ainda não implementou a rota /status-completo, devolve
 * `null` silenciosamente (o caller cai no fluxo antigo via `STATUS_PARA_ETAPA`).
 *
 * @param {{ token: string, id: number|string, signal?: AbortSignal }} args
 * @returns {Promise<object|null>}
 */
export async function buscarStatusCompletoDaEmpresa({ token, id, signal } = {}) {
  if (!token || id == null) return null;

  const endpoint = resolverEndpointStatusCompleto(id);
  if (!endpoint) return null;

  try {
    const resposta = await axios.get(endpoint, {
      headers: { Authorization: `Bearer ${token}` },
      // Não cachear: o status pode mudar a cada aprovação/reprovação.
      params: { _: Date.now() },
      signal,
    });
    return resposta.data;
  } catch (err) {
    // Cancelamento explícito (AbortController) → propaga pra cima.
    if (axios.isCancel(err)) throw err;
    // 404 (rota não disponível) → fallback silencioso.
    // Outros erros: propaga para a UI mostrar mensagem apropriada.
    if (err?.response?.status === 404) return null;
    throw err;
  }
}
